import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  doc,
  collection,
  onSnapshot,
  setDoc,
  addDoc,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import axios from 'axios';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import './FaceToFaceTracking.css';


function FaceToFaceTracking() {
  const [displayedStudentLocation, setDisplayedStudentLocation] = useState(null);
  const [displayedTutorLocation, setDisplayedTutorLocation] = useState(null);
  const { sessionId: sessionIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const auth = getAuth();
  const currentUser = auth.currentUser;
  const db = getFirestore();

  const state = location.state || {};
  const studentIdFromState = state.studentId;
  const tutorIdFromState = state.tutorId;

  const [sessionDetails, setSessionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [studentLocation, setStudentLocation] = useState(null);
  const [tutorLocation, setTutorLocation] = useState(null);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatVisible, setChatVisible] = useState(true);

  const toggleChat = () => setChatVisible((prev) => !prev);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });


  useEffect(() => {
  let animationFrame;
  const animateMarker = () => {
    if (studentLocation) {
      setDisplayedStudentLocation(prev => {
        if (!prev) return studentLocation;
        return {
          lat: prev.lat + (studentLocation.lat - prev.lat) * 0.1,
          lng: prev.lng + (studentLocation.lng - prev.lng) * 0.1
        };
      });
    }
    if (tutorLocation) {
      setDisplayedTutorLocation(prev => {
        if (!prev) return tutorLocation;
        return {
          lat: prev.lat + (tutorLocation.lat - prev.lat) * 0.1,
          lng: prev.lng + (tutorLocation.lng - prev.lng) * 0.1
        };
      });
    }
    animationFrame = requestAnimationFrame(animateMarker);
  };
  animateMarker();
  return () => cancelAnimationFrame(animationFrame);
}, [studentLocation, tutorLocation]);



  

  // 🔹 Fetch session details
  useEffect(() => {
    const fetchSessionDetails = async () => {
      if (!currentUser) {
        setError('You must be signed in to access this page.');
        setLoading(false);
        return;
      }

      try {
        const token = await currentUser.getIdToken();
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/sessions/${sessionIdParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const session = res.data;

        setSessionDetails({
          sessionId: session.id,
          courseName: session.course?.name || 'Unknown course',
          duration: session.duration,
          price: session.price,
          studentId: session.student?.id || studentIdFromState,
          tutorId: session.tutor?.id || tutorIdFromState,
          studentName: session.student?.username || 'Unknown student',
          tutorName: session.tutor?.username || 'Unknown tutor',
          meetingLocation: session.meetingLocation || { lat: null, lng: null },
          currentUserRole: currentUser.uid === session.tutor?.id ? 'tutor' : 'student',
          status: session.status,
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching session details:', err);
        setError('Failed to load session details.');
        setLoading(false);
      }
    };

    fetchSessionDetails();
  }, [sessionIdParam, currentUser, studentIdFromState, tutorIdFromState]);

  // 🔹 Subscribe to live locations (real-time updates)
  useEffect(() => {
    if (!sessionDetails) return;

    let unsubStudent, unsubTutor;

    if (sessionDetails.studentId) {
      const studentDoc = doc(db, 'liveLocations', sessionDetails.studentId);
      unsubStudent = onSnapshot(studentDoc, (snap) => {
        if (snap.exists()) setStudentLocation(snap.data());
      });
    }

    if (sessionDetails.tutorId) {
      const tutorDoc = doc(db, 'liveLocations', sessionDetails.tutorId);
      unsubTutor = onSnapshot(tutorDoc, (snap) => {
        if (snap.exists()) setTutorLocation(snap.data());
      });
    }

    return () => {
      if (unsubStudent) unsubStudent();
      if (unsubTutor) unsubTutor();
    };
  }, [sessionDetails, db]);

  // 🔹 Track current user's location and update Firestore continuously
  useEffect(() => {
    if (!currentUser) return;

    if (!navigator.geolocation) {
      console.warn('Geolocation not supported.');
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          await setDoc(
            doc(db, 'liveLocations', currentUser.uid),
            { lat: latitude, lng: longitude, updatedAt: serverTimestamp() },
            { merge: true }
          );
        } catch (err) {
          console.error('Error updating location:', err);
        }
      },
      (err) => {
        if (err.code === 1) console.warn('Location permission denied.');
        else console.error('Error getting location:', err);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 5000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [currentUser, db]);

  // 🔹 Real-time chat subscription
  useEffect(() => {
    if (!sessionIdParam) return;

    const messagesQuery = query(
      collection(db, 'sessions', sessionIdParam, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsub = onSnapshot(messagesQuery, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
    });

    return () => unsub();
  }, [sessionIdParam, db]);

  // 🔹 Send chat message
  const handleSendMessage = async () => {
    if (newMessage.trim() === '') return;

    const msg = {
      sender: currentUser.uid,
      senderName:
        currentUser.uid === sessionDetails.studentId
          ? sessionDetails.studentName
          : sessionDetails.tutorName,
      text: newMessage,
      createdAt: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, 'sessions', sessionIdParam, 'messages'), msg);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  // 🔹 End session
  const handleEndSession = async () => {
    if (!sessionDetails?.sessionId) return;

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/sessions/${sessionDetails.sessionId}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSessionDetails((prev) => ({ ...prev, status: 'completed' }));
      alert('Session ended successfully.');
      navigate('/dashboard');
    } catch (err) {
      console.error('Error ending session:', err);
      alert(err.response?.data?.error || 'Failed to end session');
    }
  };

  if (loading) return <div className="loading">Loading session details...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="face-session-container">
      <h2 className="session-title">Face-to-Face Session Tracking</h2>

      <div className="session-info">
        <p><strong>Student:</strong> {sessionDetails.studentName}</p>
        <p><strong>Tutor:</strong> {sessionDetails.tutorName}</p>
        <p><strong>Duration:</strong> {sessionDetails.duration} minutes</p>
      </div>

      {isLoaded && (studentLocation || tutorLocation) ? (
        <div className="map-container">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            zoom={14}
            center={displayedTutorLocation || displayedStudentLocation || { lat: -26.2041, lng: 28.0473 }}
          >
            {displayedStudentLocation && (
              <Marker
                position={{ lat: displayedStudentLocation.lat, lng: displayedStudentLocation.lng }}
                label="👩 Student"
              />
            )}
            {displayedTutorLocation && (
              <Marker
                position={{ lat: displayedTutorLocation.lat, lng: displayedTutorLocation.lng }}
                label="🎓 Tutor"
              />
            )}
          </GoogleMap>
        </div>
      ) : (
        <p>Waiting for live locations...</p>
      )}
      {/* Chat */}
      <div className={`chat-panel ${chatVisible ? 'visible' : 'hidden'}`}>
        <div className="chat-toggle" onClick={toggleChat}>
          {chatVisible ? '➡️' : '⬅️'}
        </div>
        <div className="chat-container">
          <h3>Chat</h3>
          <div className="chat-messages">
            {messages.length === 0 && <p className="no-messages">No messages yet</p>}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-message ${msg.sender === currentUser.uid ? 'self' : 'other'}`}
              >
                <strong>{msg.senderName}:</strong> {msg.text}
              </div>
            ))}
          </div>
          <div className="chat-input">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
            />
            <button onClick={handleSendMessage}>Send</button>
          </div>
        </div>
      </div>

      {sessionDetails.status === 'scheduled' && (
        <button onClick={handleEndSession} className="end-session-btn">
          End Session
        </button>
      )}
    </div>
  );
}

export default FaceToFaceTracking;
